import { Download, Search } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import get from "lodash/get";
import { useCallback, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import purchaseService from "../../services/purchase";
import config from "../../utils/config";
import queryDefault from "../../utils/queryDefault";
import Cookies from "js-cookie";

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      No purchases found
    </Typography>
  </Box>
);

export default function PurchaseList() {
  const navigate = useNavigate();
  const [pageSize, setPageSize] = useState<number>(10);
  const user = JSON.parse(Cookies.get("USER") || "{}");
  const [options, setOptions] = useState<any>({
    keyword: "",
    status: "",
  });

  const notification = useNotification();

  const listPurchases = useQuery(
    ["purchaseList", options],
    ({ queryKey }) => purchaseService.getAllPurchases(queryKey[1]),
    {
      ...queryDefault,
    }
  );

  const downloadPurchase = useMutation(purchaseService.downloadPurchase, {
    onSuccess: () => {
      notification.onOpen({
        message: "Purchase document was downloaded successfully!",
        type: "success",
        position: "top",
      });
    },
    onError: () => {
      notification.onOpen({
        message: "Failed to download purchase",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const handleDownload = (purchaseId: number) => {
    downloadPurchase.mutate(purchaseId);
  };

  const form = useForm({
    defaultValues: {
      keyword: "",
    },
  });

  const onHandleSearch = useCallback((values: { keyword: string }) => {
    setOptions((prev: any) => ({
      ...prev,
      keyword: values.keyword,
    }));
  }, []);

  return (
    <MainLayout>
      <Helmet>
        <title>Purchase Management</title>
      </Helmet>

      <Breadcrumb
        label="Purchase Management"
        breadcrumbs={[{ label: "Dashboard", href: "/app" }]}
      />

      {/* Only admin can access this page */}
      <ProtectedPermissionPage acceptPermissions={[1, 2]}>
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" justifyContent="space-between">
            <Stack direction="row" spacing={1}>
              <Controller
                name="keyword"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Search purchases..."
                    {...field}
                    {...config.onEnter(() => {
                      form.handleSubmit(onHandleSearch)();
                    })}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          onClick={form.handleSubmit(onHandleSearch)}
                        >
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                component={RouterLink}
                to="/app/purchase/create-purchase"
              >
                Create Purchase
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ mt: 2 }}>
          <DataGrid
            autoHeight
            loading={listPurchases.isLoading}
            getRowId={(record) => record.id}
            rows={get(listPurchases, "data.data", [])}
            pageSize={pageSize}
            rowsPerPageOptions={[10, 20, 30]}
            pagination
            disableSelectionOnClick
            onPageChange={(newPage) => {
              setOptions((prev: any) => ({ ...prev, page_number: newPage }));
            }}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            components={{
              NoRowsOverlay: CustomNoRowOverlay,
            }}
            columns={[
              {
                field: "purchase_no",
                headerName: "Purchase No",
                flex: 1,
                minWidth: 150,
                renderCell: (params) => (
                  <Button
                    variant="text"
                    onClick={() => navigate(`/app/purchase/detail/${params.row.id}`)}
                    sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
                  >
                    {params.value}
                  </Button>
                ),
              },
              {
                field: "usage_date",
                headerName: "Usage Date",
                flex: 1,
                minWidth: 120,
                renderCell: (params) => (
                  <Typography>
                    {params.value ? dayjs(params.value).format("DD/MM/YYYY") : "N/A"}
                  </Typography>
                ),
              },
              {
                field: "request_date",
                headerName: "Request Date",
                flex: 1,
                minWidth: 120,
                renderCell: (params) => (
                  <Typography>
                    {params.value ? dayjs(params.value).format("DD/MM/YYYY") : "N/A"}
                  </Typography>
                ),
              },
              {
                field: "created_by_name",
                headerName: "Created By",
                flex: 1,
                minWidth: 120,
                renderCell: (params) => (
                  <Typography>
                    {params.value}
                  </Typography>
                ),
              },
              {
                field: "status",
                headerName: "Status",
                flex: 1,
                minWidth: 120,
                renderCell: (params) => (
                  <Typography>
                    {params.value}
                  </Typography>
                ),
              },
              {
                field: "id",
                headerName: "Actions",
                sortable: false,
                flex: 1,
                minWidth: 150,
                renderCell: (params) => {
                  return (
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Download purchase">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleDownload(params.value)}
                        >
                          <Download />
                        </IconButton>
                      </Tooltip>

                      {user.role_id === 1 && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => navigate(`/app/purchase/edit/${params.value}`)}
                        >
                          Edit
                        </Button>
                      )}
                    </Stack>
                  );
                },
              },
            ]}
            sx={{
              ".MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
              },
              ".MuiDataGrid-columnHeaderTitleContainer": {
                backgroundColor: "#303c40",
                color: "white",
              },
              ".MuiDataGrid-columnHeader": {
                backgroundColor: "#303c40",
                color: "white",
              },
              ".MuiDataGrid-columnSeparator": {
                backgroundColor: "#303c40",
                color: "white",
              },
            }}
          />
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}